import { useState, useMemo } from 'react';
import { getUsageEntries, clearUsageData } from '../../../lib/aiRateLimiter';

const FEATURE_LABELS = {
  estimate_gen: 'Estimate Generation',
  chat: 'WrapMind Chat',
  followup: 'Follow-up AI',
  unknown: 'Other',
};

function fmtCost(usd) {
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return '<$0.01';
  return `$${usd.toFixed(2)}`;
}

function fmtTokens(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

// Build last 30 days of daily usage bars from entries
function buildDailyBars(entries) {
  const days = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().split('T')[0];
    days.push({ key, label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), tokens: 0, cost: 0 });
  }
  entries.forEach(e => {
    const key = e.timestamp.split('T')[0];
    const day = days.find(d => d.key === key);
    if (day) {
      day.tokens += e.tokens || 0;
      day.cost += e.costUsd || 0;
    }
  });
  return days;
}

export default function AITab() {
  const [entries, setEntries] = useState(() => getUsageEntries(30));
  const [confirmClear, setConfirmClear] = useState(false);

  const dailyBars = useMemo(() => buildDailyBars(entries), [entries]);
  const maxTokens = Math.max(...dailyBars.map(d => d.tokens), 1);

  const totalTokens = entries.reduce((s, e) => s + (e.tokens || 0), 0);
  const totalCost = entries.reduce((s, e) => s + (e.costUsd || 0), 0);

  // Per-feature breakdown
  const byFeature = useMemo(() => {
    const map = {};
    entries.forEach(e => {
      const f = e.feature || 'unknown';
      if (!map[f]) map[f] = { tokens: 0, cost: 0, calls: 0 };
      map[f].tokens += e.tokens || 0;
      map[f].cost += e.costUsd || 0;
      map[f].calls += 1;
    });
    return Object.entries(map).sort((a, b) => b[1].tokens - a[1].tokens);
  }, [entries]);

  const handleClear = () => {
    clearUsageData();
    setEntries([]);
    setConfirmClear(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[var(--wm-text-body)] font-bold text-[#0F1923] dark:text-[#F8FAFE] mb-1">AI Usage</h2>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
            Rolling 30-day usage for <span className="font-mono font-medium text-[#0F1923] dark:text-[#F8FAFE]">claude-3-5-sonnet</span>.
            Cost estimates are approximate.
          </p>
        </div>
        <div className="flex-shrink-0">
          {confirmClear ? (
            <div className="flex items-center gap-2">
              <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Are you sure?</span>
              <button
                onClick={handleClear}
                className="px-2.5 py-1 rounded-lg text-[var(--wm-text-ui)] font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={() => setConfirmClear(false)}
                className="px-2.5 py-1 rounded-lg text-[var(--wm-text-ui)] font-medium bg-gray-100 dark:bg-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-200 dark:hover:bg-[#2A3D55] transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmClear(true)}
              className="px-3 py-1.5 rounded-lg text-[var(--wm-text-ui)] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Reset Usage Data
            </button>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E]">
          <p className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#7D93AE] mb-1">Tokens This Month</p>
          <p className="text-[var(--wm-text-hero)] font-bold font-mono text-[#0F1923] dark:text-[#F8FAFE]">{fmtTokens(totalTokens)}</p>
        </div>
        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E]">
          <p className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#7D93AE] mb-1">Est. Cost This Month</p>
          <p className="text-[var(--wm-text-hero)] font-bold font-mono text-[#0F1923] dark:text-[#F8FAFE]">{fmtCost(totalCost)}</p>
        </div>
      </div>

      {/* Bar chart — last 30 days */}
      <div className="p-4 rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E]">
        <p className="text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-3 uppercase tracking-wider">Daily Token Usage (Last 30 Days)</p>
        <div className="flex items-end gap-0.5 h-20">
          {dailyBars.map((day) => {
            const heightPct = maxTokens > 0 ? (day.tokens / maxTokens) * 100 : 0;
            return (
              <div
                key={day.key}
                className="flex-1 group relative flex flex-col justify-end"
                style={{ height: '100%' }}
                title={`${day.label}: ${fmtTokens(day.tokens)} tokens`}
              >
                <div
                  className="bg-[#2E8BF0] dark:bg-[#60ABFF] rounded-t opacity-80 group-hover:opacity-100 transition-opacity min-h-[1px]"
                  style={{ height: `${Math.max(heightPct, day.tokens > 0 ? 4 : 0)}%` }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[var(--wm-text-micro)] text-[#64748B] dark:text-[#7D93AE]">{dailyBars[0]?.label}</span>
          <span className="text-[var(--wm-text-micro)] text-[#64748B] dark:text-[#7D93AE]">{dailyBars[dailyBars.length - 1]?.label}</span>
        </div>
      </div>

      {/* Per-feature breakdown */}
      {byFeature.length > 0 ? (
        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E]">
          <p className="text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-3 uppercase tracking-wider">Breakdown by Feature</p>
          <table className="w-full text-[var(--wm-text-ui)]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-[#243348]">
                <th className="text-left pb-2 font-semibold text-[#64748B] dark:text-[#7D93AE]">Feature</th>
                <th className="text-right pb-2 font-semibold text-[#64748B] dark:text-[#7D93AE]">Calls</th>
                <th className="text-right pb-2 font-semibold text-[#64748B] dark:text-[#7D93AE]">Tokens</th>
                <th className="text-right pb-2 font-semibold text-[#64748B] dark:text-[#7D93AE]">Est. Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#243348]">
              {byFeature.map(([feature, stats]) => (
                <tr key={feature}>
                  <td className="py-2 text-[#0F1923] dark:text-[#F8FAFE]">
                    {FEATURE_LABELS[feature] || feature}
                  </td>
                  <td className="py-2 text-right text-[#64748B] dark:text-[#7D93AE] font-mono">{stats.calls}</td>
                  <td className="py-2 text-right text-[#0F1923] dark:text-[#F8FAFE] font-mono">{fmtTokens(stats.tokens)}</td>
                  <td className="py-2 text-right text-[#0F1923] dark:text-[#F8FAFE] font-mono">{fmtCost(stats.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-6 rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] text-center">
          <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No AI usage recorded yet for this period.</p>
        </div>
      )}
    </div>
  );
}
